import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatBRL } from "../../lib/api";
import { hourBankName } from "../../lib/hourbank";
import { PiggyBank, ArrowDownCircle, ArrowUpCircle, Clock, ShoppingBag, CalendarClock } from "lucide-react";

const SP = { timeZone: "America/Sao_Paulo" };
const fmtD = (iso) => (iso ? new Date(iso).toLocaleDateString("pt-BR", SP) : "—");
const fmtT = (iso) => (iso ? new Date(iso).toLocaleTimeString("pt-BR", { ...SP, hour: "2-digit", minute: "2-digit" }) : "");
const fmtH = (h) => `${Number(h || 0).toFixed(1).replace(".", ",").replace(",0", "")}h`;

const STATUS = {
  active: ["bg-emerald-100 text-emerald-700", "Ativo"],
  exhausted: ["bg-gray-100 text-gray-600", "Esgotado"],
  expired: ["bg-red-100 text-red-600", "Expirado"],
};

function Movement({ m }) {
  if (m.type === "credit") {
    return (
      <div className="flex items-center justify-between gap-3 py-2.5" data-testid="statement-credit">
        <div className="flex items-center gap-2.5 min-w-0">
          <ArrowUpCircle size={16} className="text-emerald-600 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-[#14213D] truncate">Crédito — {m.plan_name || "Pacote de horas"}</div>
            <div className="text-xs text-gray-400">{fmtD(m.at)}</div>
          </div>
        </div>
        <div className="text-sm font-bold text-emerald-600 whitespace-nowrap">+{fmtH(m.hours)}</div>
      </div>
    );
  }
  if (m.type === "reserved") {
    return (
      <div className="flex items-center justify-between gap-3 py-2.5" data-testid="statement-reserved">
        <div className="flex items-center gap-2.5 min-w-0">
          <CalendarClock size={16} className="text-amber-500 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-[#14213D] truncate">
              Reserva prevista — {m.space_name || "espaço"}
            </div>
            <div className="text-xs text-gray-400">
              {fmtD(m.reservation_start)} {fmtT(m.reservation_start)} · débito no check-out
            </div>
          </div>
        </div>
        <div className="text-sm font-bold text-amber-600 whitespace-nowrap">{fmtH(m.hours)} previstas</div>
      </div>
    );
  }
  if (m.type === "adjust") {
    const pos = Number(m.hours) >= 0;
    return (
      <div className="flex items-center justify-between gap-3 py-2.5" data-testid="statement-adjust">
        <div className="flex items-center gap-2.5 min-w-0">
          {pos ? <ArrowUpCircle size={16} className="text-emerald-600 flex-shrink-0" />
               : <ArrowDownCircle size={16} className="text-amber-500 flex-shrink-0" />}
          <div className="min-w-0">
            <div className="text-sm font-semibold text-[#14213D] truncate">Ajuste administrativo</div>
            <div className="text-xs text-gray-400">
              {fmtD(m.at)}{m.reason ? ` · ${m.reason}` : ""}
              {m.balance_after != null && ` · saldo após: ${fmtH(m.balance_after)}`}
            </div>
          </div>
        </div>
        <div className={`text-sm font-bold whitespace-nowrap ${pos ? "text-emerald-600" : "text-amber-600"}`}>
          {pos ? "+" : "−"}{fmtH(Math.abs(m.hours))}
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between gap-3 py-2.5" data-testid="statement-debit">
      <div className="flex items-center gap-2.5 min-w-0">
        <ArrowDownCircle size={16} className="text-[#14213D] flex-shrink-0" />
        <div className="min-w-0">
          <div className="text-sm font-semibold text-[#14213D] truncate">
            Consumo — {m.space_name || "reserva"}
          </div>
          <div className="text-xs text-gray-400">
            {fmtD(m.reservation_start || m.at)} {fmtT(m.reservation_start || m.at)}
            {m.actual_hours_used != null && ` · ${fmtH(m.actual_hours_used)} utilizadas`}
            {m.balance_after != null && ` · saldo após: ${fmtH(m.balance_after)}`}
          </div>
        </div>
      </div>
      <div className="text-sm font-bold text-[#14213D] whitespace-nowrap">−{fmtH(m.hours)}</div>
    </div>
  );
}

export default function ExtratoBancoHoras() {
  const [pkgs, setPkgs] = useState(null);

  useEffect(() => {
    api.get("/me/hour-bank/statement").then(({ data }) => setPkgs(data)).catch(() => setPkgs([]));
  }, []);

  if (pkgs === null) return <div className="text-gray-500">Carregando extrato…</div>;

  return (
    <div data-testid="extrato-banco-horas" className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-[#D4AF37] font-semibold mb-2">Banco de Horas</div>
        <h1 className="font-display text-2xl sm:text-3xl text-[#14213D] font-light">Extrato de consumo</h1>
        <p className="text-sm text-gray-500 mt-1">Créditos, consumos por reserva e reservas previstas. O débito acontece no check-out, conforme o tempo utilizado.</p>
      </div>

      {pkgs.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-10 text-center" data-testid="extrato-empty">
          <PiggyBank size={32} className="mx-auto text-gray-300 mb-3" />
          <div className="text-gray-500 text-sm mb-4">Você ainda não possui um Banco de Horas.</div>
          <Link
            to="/portal/comprar-pacote"
            className="inline-flex items-center gap-2 bg-[#14213D] text-white px-4 py-2 rounded text-sm font-semibold hover:bg-[#1F335E]"
            data-testid="extrato-buy-link"
          >
            <ShoppingBag size={14} /> Conhecer os pacotes
          </Link>
        </div>
      ) : (
        pkgs.map((p) => {
          const pct = p.hours_total ? Math.min(100, ((p.hours_used || 0) / p.hours_total) * 100) : 0;
          const [cls, label] = STATUS[p.status] || ["bg-gray-100 text-gray-600", p.status];
          return (
            <div key={p.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={`extrato-pkg-${p.id}`}>
              <div className="px-5 py-4 border-b border-gray-100">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <PiggyBank size={16} className="text-[#D4AF37]" />
                    <span className="font-semibold text-[#14213D] text-sm">{hourBankName(p)}</span>
                    <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${cls}`}>{label}</span>
                  </div>
                  <div className="text-xs text-gray-500 flex items-center gap-1">
                    <Clock size={12} /> Validade: {fmtD(p.valid_until)}
                  </div>
                </div>
                <div className="mt-3 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-[#D4AF37]" style={{ width: `${pct}%` }} />
                </div>
                <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-500">
                  <span>{fmtH(p.hours_total)} adquiridas</span>
                  <span>{fmtH(p.hours_used)} consumidas</span>
                  <span className="font-bold text-emerald-600" data-testid={`extrato-saldo-${p.id}`}>{fmtH(p.hours_remaining)} disponíveis</span>
                </div>
              </div>
              <div className="px-5 divide-y divide-gray-50">
                {p.movements.map((m, i) => (
                  <Movement key={`${m.type}-${m.reservation_id || m.at || i}`} m={m} />
                ))}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
